import { useFetch } from "~vue/api";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type Ref, ref, type ShallowRef } from "vue";

interface ProfileSubproperty {
    subtopic_id: string;
    title: string;
    order: number;
    content: string;
}

interface ProfileTopic {
    topic_id: string;
    topic: string;
    subproperties: ProfileSubproperty[];
}

interface GeneratedProfile {
    user: string;
    topics: ProfileTopic[];
}

interface CreateProfilePayload {
    file_ids: number[];
    is_manager: boolean;
    free_text?: string;
    topics_filter?: string[];
}

interface RegenerateFromPerspectivePayload {
    regenerate_from_perspective: true;
}

type ProfilePayload = CreateProfilePayload | RegenerateFromPerspectivePayload;

interface GenerateProfileOptions {
    fileIds: number[];
    freeText?: string;
    // isRecreating is a prop used for event tracking
    isRecreating?: boolean;
    regenerateFromPerspective?: boolean;
    topicsFilter?: string[] | null;
}

interface UseProfileWizardReturn {
    generationError: Ref<string | null>;
    generateProfile: (options: GenerateProfileOptions) => Promise<void>;
    isGeneratingProfile: Readonly<ShallowRef<boolean>>;
}

export const useProfileWizard = ({ isManager, hasPerspectiveReport }: { isManager: boolean; hasPerspectiveReport: boolean }): UseProfileWizardReturn => {
    const generationError = ref<string | null>(null);

    const profilePayload = ref<ProfilePayload>({
        file_ids: [],
        is_manager: isManager,
    });

    const {
        data: profileData,
        error: profileError,
        execute: executeGenerateProfile,
        isFetching: isGeneratingProfile,
    } = useFetch("/api/accounts/profile/create_from_documents/", { immediate: false }).post(profilePayload).json<GeneratedProfile>();

    const generateProfile = async ({
        fileIds,
        freeText = "",
        isRecreating = false,
        regenerateFromPerspective = false,
        topicsFilter = null,
    }: GenerateProfileOptions): Promise<void> => {
        generationError.value = null;

        profilePayload.value = regenerateFromPerspective
            ? { regenerate_from_perspective: true }
            : {
                  file_ids: [...new Set(fileIds)],
                  is_manager: isManager,
                  free_text: freeText,
                  ...(topicsFilter && topicsFilter.length > 0 ? { topics_filter: topicsFilter } : {}),
              };

        void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_GENERATION_STARTED, {
            is_manager: isManager,
            is_recreating: isRecreating,
            regenerate_from_perspective: regenerateFromPerspective,
            is_topic_scoped: topicsFilter !== null,
            has_perspective_report: hasPerspectiveReport,
            source_file_count: fileIds.length,
            has_free_text: !!freeText,
        });

        try {
            await executeGenerateProfile();

            if (profileError.value) {
                generationError.value = "Profile generation failed. Please try again.";
            } else if (!profileData.value) {
                generationError.value = "Failed to generate profile";
            } else {
                void logUserInteraction(AmplitudeEventNames.PUBLIC_PROFILE_PUBLISHED, {
                    is_manager: isManager,
                    is_topic_scoped: topicsFilter !== null,
                    has_perspective_report: hasPerspectiveReport,
                });
            }
        } catch {
            generationError.value = "An unexpected error occurred. Please try again.";
        }
    };

    return {
        generationError,
        generateProfile,
        isGeneratingProfile,
    };
};
